// frontend/src/pages/LandingPage.js
import React from 'react';
import { useNavigate } from 'react-router-dom';

const LandingPage = () => {
  const navigate = useNavigate();

  const features = [
    {
      icon: '🔍',
      title: 'Natural Language Queries',
      description: 'Ask questions in plain English and get SQL results instantly.'
    },
    {
      icon: '🔒',
      title: 'Secure Connections',
      description: 'Connect to PostgreSQL, MySQL, and MS SQL databases securely.'
    },
    {
      icon: '⚡',
      title: 'Lightning Fast',
      description: 'Get instant results powered by advanced AI models.'
    },
    {
      icon: '🧠',
      title: 'Schema Intelligence',
      description: 'Automatically understand your database schema.'
    },
    {
      icon: '📊',
      title: 'Data Visualization',
      description: 'Visualize query results with beautiful charts.'
    },
    {/* Hide SQL File Support feature temporarily
    {
      icon: '📤',
      title: 'SQL File Support',
      description: 'Upload SQL dump files and query them instantly.'
    }
    */}
  ];

  return (
    <div className="min-h-screen bg-gray-950 overflow-hidden">
      {/* Animated Background */}
      <div className="fixed inset-0 bg-gradient-to-br from-violet-950 via-purple-900 to-gray-950">
        <div className="absolute inset-0">
          {/* Floating database icons */}
          <div className="absolute top-20 left-10 animate-float">
            <div className="w-20 h-20 bg-violet-600/20 rounded-lg backdrop-blur-sm flex items-center justify-center text-3xl">
              🗄️
            </div>
          </div>
          <div className="absolute top-40 right-20 animate-float-slow">
            <div className="w-16 h-16 bg-purple-600/20 rounded-lg backdrop-blur-sm flex items-center justify-center text-2xl">
              💾
            </div>
          </div>
          <div className="absolute bottom-40 left-1/4 animate-float-slower">
            <div className="w-24 h-24 bg-violet-700/20 rounded-lg backdrop-blur-sm flex items-center justify-center text-4xl">
              🗃️
            </div>
          </div>
          <div className="absolute top-1/3 right-1/3 animate-pulse-slow">
            <div className="w-32 h-32 bg-gradient-radial from-violet-600/30 to-transparent rounded-full blur-xl"></div>
          </div>
          <div className="absolute bottom-1/4 right-1/4 animate-pulse-slow" style={{ animationDelay: '2s' }}>
            <div className="w-40 h-40 bg-gradient-radial from-purple-600/30 to-transparent rounded-full blur-xl"></div>
          </div>
        </div>
      </div>

      {/* Navbar */}
      <nav className="relative z-10 p-6">
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          <div className="flex items-center space-x-2">
            <span className="text-3xl">🗄️</span>
            <h1 className="text-2xl font-bold bg-gradient-to-r from-violet-400 to-purple-400 bg-clip-text text-transparent">
              DB2XL
            </h1>
          </div>
          <div className="flex space-x-4">
            <button 
              onClick={() => navigate('/dashboard')}
              className="text-gray-300 hover:text-white transition-colors"
            >
              Dashboard
            </button>
            <button 
              onClick={() => navigate('/connect')}
              className="btn-primary"
            >
              Connect Database
            </button>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative z-10 max-w-7xl mx-auto px-6 pt-20 pb-32">
        <div className="text-center">
          <h2 className="text-5xl md:text-7xl font-bold mb-6">
            <span className="bg-gradient-to-r from-violet-400 via-purple-400 to-pink-400 bg-clip-text text-transparent">
              Query Your Database
            </span>
            <br />
            <span className="text-white">With Natural Language</span>
          </h2>
          <p className="text-xl text-gray-300 mb-10 max-w-3xl mx-auto">
            Transform the way you interact with databases. Ask questions in plain English 
            and get instant SQL results. No more complex query syntax or SQL errors.
          </p>
          <div className="flex justify-center space-x-4">
            <button 
              onClick={() => navigate('/dashboard')}
              className="btn-primary text-lg px-8 py-4"
            >
              Get Started Free
            </button>
            {/* Hide Upload SQL File button temporarily
            <button 
              onClick={() => navigate('/upload')}
              className="btn-secondary text-lg px-8 py-4"
            >
              Upload SQL File
            </button>
            */}
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="relative z-10 max-w-7xl mx-auto px-6 py-20">
        <div className="text-center mb-16">
          <h3 className="text-4xl font-bold mb-4">
            <span className="bg-gradient-to-r from-violet-400 to-purple-400 bg-clip-text text-transparent">
              Powerful Features
            </span>
          </h3>
          <p className="text-xl text-gray-400">Everything you need to query databases effortlessly</p>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {features.map((feature, index) => (
            <div 
              key={index}
              className="card-gradient p-6 hover:bg-violet-800/20 transition-all duration-300 hover:scale-105 hover:border-violet-700/50"
            >
              <div className="text-4xl mb-4">{feature.icon}</div>
              <h4 className="text-xl font-semibold mb-2 text-violet-300">{feature.title}</h4>
              <p className="text-gray-400">{feature.description}</p>
            </div>
          ))}
        </div>
      </section>

      {/* How It Works */}
      <section className="relative z-10 max-w-7xl mx-auto px-6 py-20">
        <div className="text-center mb-16">
          <h3 className="text-4xl font-bold mb-4">
            <span className="bg-gradient-to-r from-violet-400 to-purple-400 bg-clip-text text-transparent">
              How It Works
            </span>
          </h3>
          <p className="text-xl text-gray-400">Get started in 3 simple steps</p>
        </div>
        
        <div className="flex flex-col md:flex-row justify-center items-center space-y-8 md:space-y-0 md:space-x-8">
          <div className="text-center max-w-xs">
            <div className="w-20 h-20 mx-auto mb-4 bg-gradient-to-r from-violet-600 to-purple-600 rounded-full flex items-center justify-center text-2xl font-bold">
              1
            </div>
            <h4 className="text-xl font-semibold mb-2">Connect Database</h4>
            <p className="text-gray-400">Securely connect to your database</p>
          </div>
          
          <div className="hidden md:block text-4xl text-violet-600">→</div>
          
          <div className="text-center max-w-xs">
            <div className="w-20 h-20 mx-auto mb-4 bg-gradient-to-r from-violet-600 to-purple-600 rounded-full flex items-center justify-center text-2xl font-bold">
              2
            </div>
            <h4 className="text-xl font-semibold mb-2">Ask Questions</h4>
            <p className="text-gray-400">Type questions in plain English naturally</p>
          </div>
          
          <div className="hidden md:block text-4xl text-violet-600">→</div>
          
          <div className="text-center max-w-xs">
            <div className="w-20 h-20 mx-auto mb-4 bg-gradient-to-r from-violet-600 to-purple-600 rounded-full flex items-center justify-center text-2xl font-bold">
              3
            </div>
            <h4 className="text-xl font-semibold mb-2">Get Results</h4>
            <p className="text-gray-400">View results in tables or export to Excel</p>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="relative z-10 py-20">
        <div className="max-w-4xl mx-auto text-center px-6">
          <div className="card-gradient p-12">
            <h3 className="text-4xl font-bold mb-4">
              <span className="bg-gradient-to-r from-violet-400 to-purple-400 bg-clip-text text-transparent">
                Ready to Transform Your Database Experience?
              </span>
            </h3>
            <p className="text-xl text-gray-300 mb-8">
              Start querying your databases with natural language today
            </p>
            <div className="flex justify-center space-x-4">
              <button 
                onClick={() => navigate('/dashboard')}
                className="btn-primary text-lg px-8 py-4"
              >
                Start Free Trial
              </button>
              <button 
                onClick={() => navigate('/connect')}
                className="btn-secondary text-lg px-8 py-4"
              >
                Connect Database
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="relative z-10 py-8 border-t border-violet-900/50">
        <div className="max-w-7xl mx-auto px-6 text-center text-gray-400">
          <p>© 2024 DB2XL. Query databases with natural language.</p>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;